import React from "react"; 
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Header from "./Component/Header";
import PrivateRoute from "./PrivateRoute";
import RoleBasedRoute from "./RoleBasedRoute";

// Import all your existing pages
import Index from "./Pages/Index";
import CreateReportForm from "./Pages/CreateReportForm";
import ClientQueriesTable from "./Pages/ClientQueriesTable";
import EmployeeCards from "./Pages/EmployeeCards";
import IncentiveConfiguration from "./Pages/IncentiveConfiguration";
import ReportSubmission from "./Pages/ReportSubmissionTable";
import StoreManagementApp from "./Pages/StoreManagementForm";
import StoreReportsTable from "./Pages/StoreReportsTable";
import SuspiciousTransactionAlerts from "./Pages/SuspiciousTransactionAlerts";
import TriggerAlerts from "./Pages/TriggerAlerts";
import SurvillEmployeeLogin from "./Pages/SurvillEmployeeLogin";
import CreateEditReportForm from "./Pages/CreateEditReportForm";
import SingleTransactionPage from "./Pages/SingleTransactionPage";
import StoresPage from "./Pages/StoresPage";
import EmployeeManagementForm from "./Pages/EmployeeManagementForm"; 
import StoreManagementForm from "./Pages/StoreManagementForm";
import SingleStorePage from "./Pages/SingleStorePage";
import EmployeePage from "./Pages/EmployeePage";
import GlobalLoader from './Component/GlobalLoader';
import ClientQueriesPage from "./Pages/ClientQueriesPage";
import CreateEditClientQueryForm from "./Pages/CreateEditClientQueryForm";
import StoreNotesPage from "./Pages/StoreNotesPage";
import NotificationsPage from "./Pages/NotificationsPage";
import AnnouncementsPage from "./Pages/AnnouncementsPage";
import DeviceManagement from "./Pages/DeviceManagement";

// NEW: Import Attendance & Leave Management Components

import LeaveApplication from "./Component/LeaveApplication";
import LeaveStatus from "./Component/LeaveStatus";
import HRLeaveManagement from "./Component/HRLeaveManagement";
import TodayAttendanceDashboard from "./Component/TodayAttendanceDashboard";
import MyAttendance from "./Component/MyAttendance";
import EmployeeAttendanceSummary from "./Component/EmployeeAttendanceSummary";

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

// Extract the logic to a separate component so we can use the `useLocation` hook
function AppContent() {
  const location = useLocation();
  const { pathname } = location;
  
  // hide header on login, "new report", "edit report", "store management app", "store edit", and "employee management" pages
  const isLogin = pathname === "/login";
  const isNewReport = pathname === "/reports/new";
  const isEditReport = /^\/reports\/[^/]+\/edit$/.test(pathname);
  const isStoreManagement = pathname === "/storemanagementapp";
  const isStoreEdit = /^\/stores\/[^/]+\/edit$/.test(pathname);
  const isNewEmployee = pathname === "/employees/new";
  const isEditEmployee = /^\/employees\/[^/]+\/edit$/.test(pathname);
  const isNewClientQuery = pathname === "/client-queries/new";
  const isEditClientQuery = /^\/client-queries\/[^/]+\/edit$/.test(pathname);

  const shouldHideHeader = isLogin || isNewReport || isEditReport || isStoreManagement || isStoreEdit || isNewEmployee || isEditEmployee || isNewClientQuery || isEditClientQuery;
  
  
  // Define pages where GlobalLoader should be shown
  const shouldShowLoader = () => {
    // Show loader on these specific pages
    const loaderPages = [
      "/reports/new",                    // Create report page
      /^\/reports\/[^/]+\/edit$/,        // Edit report page
      /^\/transaction\/[^/]+$/,          // Single transaction page
      /^\/stores\/[^/]+$/,               // Single store page
      /^\/employees\/[^/]+$/,            // Single employee page
      "/employees/new",                  // Create employee page
      /^\/employees\/[^/]+\/edit$/,      // Edit employee page
      "/storemanagementapp",             // Store management app
      /^\/stores\/[^/]+\/edit$/,         // Store edit page
      "/createreportform",               // Create report form (if you want it here too)
      "/suspicioustransactionalerts",    // Suspicious transaction alerts
      "/client-queries/new",             // Create client query page
      /^\/client-queries\/[^/]+\/edit$/, // Edit client query page
    ];
    
    return loaderPages.some(page => {
      if (typeof page === 'string') {
        return pathname === page;
      } else if (page instanceof RegExp) {
        return page.test(pathname);
      }
      return false;
    });
  };
  
  return (
    <div className="App">
      {!shouldHideHeader && <Header />}
      
      {/* Conditionally render GlobalLoader only on specific pages */}
      {shouldShowLoader() && <GlobalLoader />}
      
      <Routes>
        <Route path="/login" element={<SurvillEmployeeLogin />} />
        
        <Route element={<PrivateRoute />}>
          <Route path="/" element={<Index />} />
          <Route path="/dashboard" element={<Index />} />
          <Route path="/client-queries/new" element={<CreateEditClientQueryForm />} />
          <Route path="/client-queries/:queryId/edit" element={<CreateEditClientQueryForm />} />
          <Route path="/createreportform" element={<CreateReportForm />} />
          <Route path="/clientqueriestable" element={<ClientQueriesPage />} />
          
          {/* Employee Cards - Team Leader and above only */}
          <Route 
            path="/employeecards" 
            element={
              <RoleBasedRoute minRank={4}>
                <EmployeeCards />
              </RoleBasedRoute>
            } 
          />
          
          {/* Stores Page - Admin only */}
          <Route 
            path="/stores" 
            element={
              <RoleBasedRoute adminOnly={true}>
                <StoresPage />
              </RoleBasedRoute>
            } 
          />
          
          <Route path="/incentiveconfiguration" element={<IncentiveConfiguration />} />
          <Route path="/reportsubmissiontable" element={<ReportSubmission />} />
          
          {/* Store Management - Admin only */}
          <Route 
            path="/storemanagementapp" 
            element={
              <RoleBasedRoute adminOnly={true}>
                <StoreManagementApp />
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/stores/:storeId/edit" 
            element={
              <RoleBasedRoute adminOnly={true}>
                <StoreManagementForm />
              </RoleBasedRoute>
            } 
          />
          
          <Route path="/storereportstable" element={<StoreReportsTable />} />
          <Route path="/suspicioustransactionalerts" element={<SuspiciousTransactionAlerts />} />
          <Route path="/triggeralerts" element={<TriggerAlerts />} />
          <Route path="/reports/new" element={<CreateEditReportForm />} />
          <Route path="/reports/:reportId/edit" element={<CreateEditReportForm />} />
          <Route path="/transaction/:transactionId" element={<SingleTransactionPage />} />
          <Route path="/stores/:storeId" element={<SingleStorePage />} />
          
          {/* Employee Management - Admin only */}
          <Route 
            path="/employees/new" 
            element={
              <RoleBasedRoute adminOnly={true}>
                <EmployeeManagementForm />
              </RoleBasedRoute>
            } 
          />
          <Route 
            path="/employees/:employeeId/edit" 
            element={
              <RoleBasedRoute adminOnly={true}>
                <EmployeeManagementForm />
              </RoleBasedRoute>
            } 
          />
          
          {/* Employee Page - Accessible to all (users can view their own page) */}
          <Route path="/employees/:employeeId" element={<EmployeePage />} />
          <Route path="/store-notes" element={<StoreNotesPage />} />
          
          {/* Notifications Page - Accessible to all authenticated users */}
          <Route path="/notifications" element={<NotificationsPage />} />
          
          {/* Announcements Page - Accessible to all authenticated users */}
          <Route path="/announcements" element={<AnnouncementsPage />} />
          
          {/* Device Management - Admin only */}
          <Route 
            path="/device-management" 
            element={
              <RoleBasedRoute adminOnly={true}>
                <DeviceManagement />
              </RoleBasedRoute>
            } 
          />

          {/* ==================== ATTENDANCE ROUTES ==================== */}
          
          {/* Attendance Dashboard - Team Leader and above */}
          <Route 
            path="/admin/attendance" 
            element={
              <RoleBasedRoute minRank={4}>
                <TodayAttendanceDashboard />
              </RoleBasedRoute>
            } 
          />
          
          {/* Employee Attendance Summary - Team Leader and above */}
          <Route 
            path="/admin/summary" 
            element={
              <RoleBasedRoute minRank={4}>
                <EmployeeAttendanceSummary />
              </RoleBasedRoute>
            } 
          />
          
          {/* Employee's own attendance - Accessible to all */}
          <Route 
            path="/employee/attendance" 
            element={<MyAttendance />} 
          />

          {/* ==================== LEAVE MANAGEMENT ROUTES ==================== */}
          
          {/* Apply for Leave - Accessible to all employees */}
          <Route 
            path="/employee/leave/apply" 
            element={<LeaveApplication />} 
          />
          
          {/* View Leave Status - Accessible to all employees */}
          <Route 
            path="/employee/leave/status" 
            element={<LeaveStatus />} 
          />
          
          {/* HR Leave Management - Team Leader and above (for approvals) */}
          <Route 
            path="/admin/leaves" 
            element={
              <RoleBasedRoute minRank={4}>
                <HRLeaveManagement />
              </RoleBasedRoute>
            } 
          />

        </Route>
      </Routes>
    </div>
  );
}

export default App;